import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

const BACKEND_URL = config.BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadPage = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const { isAdmin, getAuthHeader, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAdmin()) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/');
    }
  }, [isAdmin, loading, navigate]);

  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center">
      <div className="text-white">Loading...</div>
    </div>;
  }

  if (!isAdmin()) {
    return null;
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/tools/upload-file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeader()
        },
        timeout: 300000 // 5 minutes timeout for large files
      });

      setResult(response.data);
      toast.success(`Successfully processed ${response.data.processed_count} tools!`);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen px-6 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="gradient-text">Bulk Import</span>
          </h1>
          <p className="text-lg text-gray-400">Streamline tool onboarding with automated bulk uploads and AI-powered metadata extraction</p>
        </div>

        {/* Instructions */}
        <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-2xl p-6 mb-8 shadow-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-300 mb-2">File Format & Requirements</h3>
              <ul className="text-sm text-gray-400 space-y-1.5 leading-relaxed">
                <li>• Submit a plain text file (.txt) with tool URLs</li>
                <li>• One URL per line or space-separated format supported</li>
                <li>• Accepts http:// and https:// protocols</li>
                <li>• AI automatically extracts metadata: name, description, icons, and taxonomy</li>
                <li>• <span className="text-purple-400 font-medium">Unlimited tool uploads</span> - process hundreds at once</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-8">
          <div className="space-y-6">
            {/* File Input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Select File</label>
              <div className="relative">
                <input
                  data-testid="file-input"
                  type="file"
                  accept=".txt"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="flex items-center justify-center gap-3 w-full px-6 py-12 border-2 border-dashed border-gray-600 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-gray-700/20 transition-all"
                >
                  <FileText className="w-8 h-8 text-gray-400" />
                  <div className="text-center">
                    <p className="text-gray-300 font-medium">
                      {file ? file.name : 'Click to select a text file'}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {file ? `${(file.size / 1024).toFixed(2)} KB` : 'Supported: .txt files'}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Upload Button */}
            <button
              data-testid="upload-btn"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-blue-500/30"
            >
              {uploading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Upload & Process
                </>
              )}
            </button>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className="mt-8 bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-8 fade-in">
            <h3 className="text-2xl font-bold mb-6">Upload Results</h3>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <div>
                  <div className="text-2xl font-bold text-green-400">{result.processed_count}</div>
                  <div className="text-sm text-gray-400">Successfully Processed</div>
                </div>
              </div>
              
              {result.failed_count > 0 && (
                <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <XCircle className="w-6 h-6 text-red-400" />
                  <div>
                    <div className="text-2xl font-bold text-red-400">{result.failed_count}</div>
                    <div className="text-sm text-gray-400">Failed</div>
                  </div>
                </div>
              )}
            </div>

            {result.failed_urls && result.failed_urls.length > 0 && (
              <div className="mt-4">
                <h4 className="text-lg font-semibold mb-2">Failed URLs:</h4>
                <div className="bg-gray-900/50 rounded-lg p-4 max-h-48 overflow-y-auto">
                  {result.failed_urls.map((url, index) => (
                    <div key={index} className="text-sm text-gray-400 py-1">
                      {url}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex gap-4">
              <button
                data-testid="view-tools-btn"
                onClick={() => window.location.href = '/browse'}
                className="px-6 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
              >
                View All Tools
              </button>
              <button
                data-testid="upload-another-btn"
                onClick={() => {
                  setFile(null);
                  setResult(null);
                  document.getElementById('file-upload').value = '';
                }}
                className="px-6 py-3 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-medium transition-colors"
              >
                Upload Another File
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadPage;